import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Store,
  Plus,
  Search,
  Edit,
  Trash2,
  QrCode,
  ExternalLink,
  X,
  CheckCircle2,
  Download,
  Printer,
  ChevronDown,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { apiBoss } from "@/lib/api";

import DragDropImageUpload from "@/components/common/DragDropImageUpload";
import { VerifiedMobileInput } from "@/components/common/VerifiedInput";

const EMPTY_CAFE = {
  cafe_name: "",
  owner_name: "",
  mobile: "",
  email: "",
  location: "",
  maps_url: "",
  table_count: 15,
  logo_url: "",
  cover_image_url: "",
  description: "",
  website_url: "",
  instagram_url: "",
  menu_url: "",
  qr_url: "",
  status: "Active",
};

export default function BossCafesPage() {
  const [cafes, setCafes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_CAFE);

  // QR Modal state
  const [qrCafe, setQrCafe] = useState(null);
  const [selectedTable, setSelectedTable] = useState(1);

  const loadCafes = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.append("search", search.trim());
    if (statusFilter !== "All") params.append("status", statusFilter);

    apiBoss.get(`/boss/cafes?${params.toString()}`)
      .then(({ data }) => setCafes(data))
      .catch(() => toast.error("Failed to load cafes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCafes();
  }, [search, statusFilter]);

  const openAddModal = () => {
    setEditingId(null);
    setForm(EMPTY_CAFE);
    setShowModal(true);
  };

  const openEditModal = (cafe) => {
    setEditingId(cafe.id);
    setForm({
      cafe_name: cafe.cafe_name || "",
      owner_name: cafe.owner_name || "",
      mobile: cafe.mobile || "",
      email: cafe.email || "",
      location: cafe.location || "",
      maps_url: cafe.maps_url || "",
      table_count: cafe.table_count || 10,
      logo_url: cafe.logo_url || "",
      cover_image_url: cafe.cover_image_url || "",
      description: cafe.description || "",
      website_url: cafe.website_url || "",
      instagram_url: cafe.instagram_url || "",
      menu_url: cafe.menu_url || "",
      qr_url: cafe.qr_url || "",
      status: cafe.status || "Active",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.cafe_name.trim() || !form.owner_name.trim() || !form.mobile.trim() || !form.location.trim()) {
      return toast.error("Please fill required fields (Cafe Name, Owner, Mobile, Location)");
    }

    try {
      if (editingId) {
        await apiBoss.put(`/boss/cafes/${editingId}`, form);
        toast.success(`Cafe '${form.cafe_name}' updated successfully!`);
      } else {
        await apiBoss.post("/boss/cafes", form);
        toast.success(`Cafe '${form.cafe_name}' created successfully!`);
      }
      setShowModal(false);
      loadCafes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save cafe record.");
    }
  };

  const handleDelete = async (cafe) => {
    if (!window.confirm(`Are you sure you want to delete "${cafe.cafe_name}"?`)) return;
    try {
      await apiBoss.delete(`/boss/cafes/${cafe.id}`);
      toast.success(`Deleted '${cafe.cafe_name}'`);
      loadCafes();
    } catch (err) {
      toast.error("Failed to delete cafe.");
    }
  };

  const getFullMenuUrl = (cafe, table) => {
    const origin = window.location.origin;
    const path = cafe.menu_url || `/menu/${cafe.slug}`;
    const url = `${origin}${path}`;
    return table ? `${url}?table=${table}` : url;
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Database Curation</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Cafe & Restaurant Management</h1>
        </div>

        <button
          onClick={openAddModal}
          className="h-12 px-6 rounded-2xl bg-primary text-white text-sm font-semibold flex items-center gap-2 hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all"
        >
          <Plus className="w-4 h-4" />
          <span>Add New Cafe</span>
        </button>
      </div>

      {/* Search & Filter Controls */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-4 rounded-3xl">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by cafe name, owner, location, mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 pl-11 pr-4 rounded-2xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* Status Filters */}
        <div className="flex gap-1.5 overflow-x-auto w-full sm:w-auto">
          {["All", "Active", "Inactive", "Completed", "Pending"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`h-11 px-4 rounded-2xl text-xs font-semibold whitespace-nowrap transition-all border ${
                statusFilter === st
                  ? "bg-primary text-white border-primary"
                  : "bg-slate-950 text-slate-400 border-slate-800 hover:text-white"
              }`}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Cafe List Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-950 text-slate-400 uppercase font-mono border-b border-slate-800">
              <tr>
                <th className="py-4 px-6">Cafe Name</th>
                <th className="py-4 px-6">Owner</th>
                <th className="py-4 px-6">Mobile</th>
                <th className="py-4 px-6">Location</th>
                <th className="py-4 px-6 text-center">Tables</th>
                <th className="py-4 px-6">Status</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-800/70">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 font-mono">Loading Cafes...</td>
                </tr>
              ) : cafes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500 font-mono">No cafes found matching filters.</td>
                </tr>
              ) : (
                cafes.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/40 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <img
                          src={c.logo_url || "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=100&auto=format&fit=crop"}
                          alt="logo"
                          className="w-10 h-10 rounded-xl object-cover border border-slate-800"
                        />
                        <div>
                          <div className="font-bold text-white text-sm">{c.cafe_name}</div>
                          <a
                            href={`/menu/${c.slug}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] text-primary hover:underline font-mono"
                          >
                            /menu/{c.slug}
                          </a>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 font-medium text-white">{c.owner_name}</td>
                    <td className="py-4 px-6 font-mono text-slate-400">{c.mobile}</td>
                    <td className="py-4 px-6">{c.location}</td>
                    <td className="py-4 px-6 text-center font-mono font-bold text-white">{c.table_count}</td>
                    <td className="py-4 px-6">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-mono font-semibold ${
                        c.status === "Active" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-slate-800 text-slate-400"
                      }`}>
                        {c.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* QR Generator Button */}
                        <button
                          onClick={() => {
                            setQrCafe(c);
                            setSelectedTable(1);
                          }}
                          className="p-2 rounded-xl bg-primary/20 text-primary hover:bg-primary hover:text-white transition-colors"
                          title="Generate Table QR Codes"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => openEditModal(c)}
                          className="p-2 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                          title="Edit Cafe Details"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => handleDelete(c)}
                          className="p-2 rounded-xl bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white transition-colors"
                          title="Delete Cafe"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD / EDIT CAFE MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl p-6 sm:p-8 space-y-6 shadow-2xl my-8">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <h2 className="font-heading text-2xl font-bold text-white">
                {editingId ? "Edit Cafe Details" : "Add New Cafe / Restaurant"}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-xl text-slate-400 hover:bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Cafe Name *</label>
                  <input
                    type="text"
                    required
                    value={form.cafe_name}
                    onChange={(e) => setForm({ ...form, cafe_name: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Owner Name *</label>
                  <input
                    type="text"
                    required
                    value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <VerifiedMobileInput
                  label="Owner Mobile * (10-Digit)"
                  value={form.mobile}
                  onChange={(val) => setForm({ ...form, mobile: val })}
                />
                <div>
                  <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Location *</label>
                  <input
                    type="text"
                    required
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Number of Tables *</label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={form.table_count}
                    onChange={(e) => setForm({ ...form, table_count: parseInt(e.target.value) || 1 })}
                    className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <DragDropImageUpload
                  label="Cafe Logo Photo (Drag & Drop)"
                  value={form.logo_url}
                  onChange={(val) => setForm({ ...form, logo_url: val })}
                  placeholder="Drag & drop logo photo or click"
                />
                <DragDropImageUpload
                  label="Cafe Cover Photo (Drag & Drop)"
                  value={form.cover_image_url}
                  onChange={(val) => setForm({ ...form, cover_image_url: val })}
                  placeholder="Drag & drop cover photo or click"
                />
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Short Description</label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full p-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-mono uppercase text-slate-400 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="h-11 px-5 rounded-xl border border-slate-800 text-xs font-semibold text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="h-11 px-6 rounded-xl bg-primary text-white text-xs font-semibold hover:bg-primary/90"
                >
                  Save Cafe Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TABLE QR CODE GENERATOR MODAL */}
      {qrCafe && (
        <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-md p-6 space-y-6 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-primary" />
                <h3 className="font-heading text-lg font-bold text-white">QR Code Generator</h3>
              </div>
              <button onClick={() => setQrCafe(null)} className="p-1.5 rounded-xl text-slate-400 hover:bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="font-bold text-white text-base">{qrCafe.cafe_name}</div>
                <div className="text-xs text-slate-400">{qrCafe.location} • Total Tables: {qrCafe.table_count}</div>
              </div>

              {/* Table Selector */}
              <div>
                <label className="block text-xs font-mono uppercase text-slate-400 mb-1">Select Table Number</label>
                <select
                  value={selectedTable}
                  onChange={(e) => setSelectedTable(parseInt(e.target.value))}
                  className="w-full h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white font-mono"
                >
                  {[...Array(qrCafe.table_count)].map((_, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      Table #{idx + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* Generated QR Code Card Container */}
              <div className="bg-white text-slate-900 rounded-3xl p-6 text-center space-y-4 shadow-xl border border-slate-200">
                <div className="text-xs font-mono uppercase tracking-widest text-primary font-bold">TABLEZY QR CARD</div>
                <h4 className="font-heading text-xl font-bold">{qrCafe.cafe_name}</h4>

                <div className="p-3 bg-slate-900 rounded-2xl w-fit mx-auto shadow-md">
                  <QRCodeSVG
                    value={getFullMenuUrl(qrCafe, selectedTable)}
                    size={160}
                    bgColor="#ffffff"
                    fgColor="#0f172a"
                    level="H"
                    includeMargin={true}
                  />
                </div>

                <div className="inline-block bg-slate-900 text-white font-mono text-sm px-4 py-1.5 rounded-full font-bold">
                  Table #{selectedTable}
                </div>
                <p className="text-[11px] text-slate-500 font-mono">Scan camera to view digital menu</p>
              </div>

              <div className="text-xs text-slate-400 font-mono text-center truncate">
                Target URL: <a href={getFullMenuUrl(qrCafe, selectedTable)} target="_blank" className="text-primary hover:underline">{getFullMenuUrl(qrCafe, selectedTable)}</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
